/*
 * Copyright (c) 2023 - 2026. Bluemate B.V.
 */

import React, {useContext, useEffect, useRef, useState} from 'react';
import {preventDefault, ResponseData, validateEmail} from '@dvrd/dvr-controls';
import {CredentialResponse} from '@react-oauth/google';
import {AppContext} from '../../misc/appContext';
import {googleLogin, login} from '../../../util/requestSender';
import User from '../../../models/userModel';
import {ActivateHandle, LoginErrors} from '../../../util/interfaces';
import {setSentryUser} from '../../../util/sentryUtil';
import {sendGaEvent, sendGaPageView, setGaUserId} from '../../../util/analytics';
import Login from './login';
import {setNavigationParam} from '../../../util/navigateUtil';
import ValidateTwoFactorController from '../../twoFactor/validate/validateTwoFactorController';
import {showValidationErrors} from '../../../util/componentUtil';
import {useLocation, useNavigate} from 'react-router';
import {isSafeRedirectPath} from '../../../routes';
import {DASHBOARD_ROUTE, FORGOT_PASSWORD_ROUTE, LOGIN_ROUTE, REGISTER_ROUTE} from '../../../util/constants';
import IpVerificationController from '../ipVerification/ipVerificationController';
import {getFingerprint} from '../../../util/utils';
import {showDialog} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';
import {
    clearSessionBannerDismissals,
    deleteFromSession,
    getFromSession,
    PUSH_EMAIL_KEY,
    saveToSession
} from '../../../util/storageUtil';

const emptyErrors: LoginErrors = {email: null, password: null, general: null};

// A push-click prefills the login email via ?push_email=… and then strips that
// param from the URL. Persisting it lets the prefill survive a reload — e.g.
// connectionRecovery reloading to re-pass a Cloudflare challenge — instead of
// the field silently clearing. Cleared on a successful login and on logout.

/** Read & persist the live ?push_email= param. Returns the valid email or null.
 *  Shared by the lazy initial state and the search-change effect so both stay in
 *  sync if the validation rule ever changes. */
function persistPushEmail(search: string): string | null {
    const fromUrl = new URLSearchParams(search).get('push_email');
    if (fromUrl && validateEmail(fromUrl)) {
        saveToSession(PUSH_EMAIL_KEY, fromUrl);
        return fromUrl;
    }
    return null;
}

/** Resolve the push-handoff email prefill: prefer the live ?push_email= param,
 *  else fall back to a value persisted before a reload. */
function readPushEmailPrefill(search: string): string {
    const fromUrl = persistPushEmail(search);
    if (fromUrl) return fromUrl;
    const stored = getFromSession(PUSH_EMAIL_KEY);
    return stored && validateEmail(stored) ? stored : '';
}

function determineRedirect(): string | null {
    const redirect = new URLSearchParams(location.search).get('redirect');
    return isSafeRedirectPath(redirect) ? redirect : null;
}

export default function LoginController() {
    const context = useContext(AppContext);
    const {t} = useTranslation();
    // Pre-fill from a push-click handoff (?push_email=…) so the user only needs to
    // type their password when a notification requires switching account / re-login.
    const [email, setEmail] = useState(() => readPushEmailPrefill(location.search));
    const [password, setPassword] = useState('');
    const [errors, setErrors] = useState<LoginErrors>(emptyErrors);
    const [loading, setLoading] = useState(false);
    const mfaRef = useRef<ActivateHandle>(null);
    const ipVerificationRef = useRef<ActivateHandle>(null);
    const navigate = useNavigate();
    const reactLocation = useLocation();
    const redirectURL = useRef<string | null>(determineRedirect());

    // The lazy useState above pre-fills on mount, but when the /inloggen tab is
    // ALREADY open a push-click navigates client-side (URL changes, no remount),
    // so re-apply the prefill whenever ?push_email=… appears/changes.
    useEffect(() => {
        const prefill = persistPushEmail(reactLocation.search);
        if (prefill) setEmail(prefill);
    }, [reactLocation.search]);

    function onGoogleLogin(response: CredentialResponse) {
        const token = response.credential;
        if (!token) return onGoogleError();
        setLoading(true);
        getFingerprint().then((fingerprint: string) => {
            googleLogin({
                data: {token, fp: fingerprint},
                callback: (data: ResponseData) => {
                    requestCallback(data);
                },
                errorCallback: onConnectionError
            });
        });
    }

    function onGoogleError() {
        showDialog(
            t('auth.login.googleErrorMessage', 'Er is iets missgegaan bij het verbinden met Google.'),
            t('auth.login.loginFailedTitle', 'Inloggen mislukt')
        );
    }

    function onChange(key: 'email' | 'password') {
        return function (value: string) {
            if (key === 'email') setEmail(value);
            else setPassword(value);
            setErrors({...errors, [key]: null});
        };
    }

    function onLogin() {
        onSubmit();
    }

    function onSubmit(params?: {email?: string; password?: string}) {
        sendGaEvent('Click', 'Inloggen', 'Log in');
        if (!validateValues(params)) return;
        setLoading(true);
        const _email = params?.email ?? email;
        const _password = params?.password ?? password;
        getFingerprint().then((fingerprint: string) => {
            login({
                data: {email: _email, password: _password, fp: fingerprint},
                callback: (data: ResponseData) => {
                    requestCallback(data);
                },
                errorCallback: onConnectionError
            });
        });
    }

    // A blocked/failed request (e.g. a Cloudflare 403 after the app sat idle)
    // would otherwise never resolve the spinner — bluemateFetch swallows the
    // non-2xx and the success callback never fires. Reset loading and show a
    // retryable error. connectionRecovery may also reload to re-pass the
    // challenge; if it does, this is moot. Shared by manual + Google login.
    function onConnectionError() {
        setLoading(false);
        setErrors(prev => ({
            ...prev,
            general: t('auth.login.connectionError', 'Verbinding mislukt — controleer je internet en probeer opnieuw.'),
            generalIsCredentials: false
        }));
    }

    function onFocusField(key: keyof LoginErrors) {
        return function () {
            setErrors({...errors, [key]: null});
        };
    }

    function onClickRegister() {
        sendGaEvent('Click', 'Inloggen', 'Registreer nu');
        navigate(REGISTER_ROUTE);
    }

    function onClickGoogle() {
        sendGaEvent('Click', 'Inloggen', 'Google login');
    }

    function onClickReset(evt: React.MouseEvent) {
        preventDefault(evt);
        setNavigationParam(email);
        navigate(FORGOT_PASSWORD_ROUTE);
    }

    function onTwoFactorSuccess(user: Record<string, any>) {
        requestCallback({user, success: true, status: 'success'});
    }

    function validateValues(params?: {email?: string; password?: string}): boolean {
        let valid = true;
        const _errors = {...errors};
        const _email = params?.email ?? email;
        const _password = params?.password ?? password;
        if (!_email.length) {
            _errors.email = t('auth.login.emailRequired', 'Vul je e-mailadres in');
            valid = false;
        } else if (!validateEmail(_email)) {
            _errors.email = t('auth.login.emailInvalid', 'Vul een geldig e-mailadres in');
            valid = false;
        } else if (!_password.length) {
            _errors.password = t('auth.login.passwordRequired', 'Vul je wachtwoord in');
            valid = false;
        }
        if (!valid) setErrors(_errors);
        return valid;
    }

    function requestCallback(data: ResponseData) {
        setLoading(false);
        if (!data.success) {
            if (data.type === 'blocked' && data.message)
                return showDialog(data.message, t('auth.login.accountBlockedTitle', 'Account geblokkeerd'));
            else if (data.type === 'validation_error' && data.errors) return showValidationErrors(data.errors);
            else if (data.type === 'google_account')
                return showDialog(
                    t(
                        'auth.login.googleAccountMessage',
                        'Dit e-mailadres is geregistreerd met Google. Je kunt inloggen via de Google inloggen knop.'
                    ),
                    t('auth.login.googleAccountTitle', 'Google account')
                );
            else
                return setErrors({
                    ...errors,
                    general: data.message ?? t('auth.login.genericError', 'Er is iets misgegaan'),
                    generalIsCredentials: true
                });
        }
        successCallback(data);
    }

    function successCallback(data: ResponseData) {
        // Email+password accepted — drop the persisted push prefill so a later
        // logout/login in this session starts clean.
        deleteFromSession(PUSH_EMAIL_KEY);
        clearSessionBannerDismissals();
        if (data.two_factor_id)
            // Requires two-factor authentication
            return mfaRef.current?.activate(data.two_factor_id);
        else if (data.verification_id)
            // Requires IP verification
            return ipVerificationRef.current?.activate(data.verification_id, email);
        const user = data.user instanceof User ? data.user : new User(data.user);
        setGaUserId(user.id);
        const firstAccount = user.accounts.length ? user.accounts[0] : null;
        context.onChangeContext({
            user,
            accounts: user.accounts,
            account: firstAccount
        });
    }

    function checkDemoLogin() {
        const params = new URLSearchParams(location.search);
        if (params.get('demo') !== '1') return;
        const email = 'demo@bluemate.nl';
        const password = 'bolmate_demo';
        setEmail(email);
        setPassword(password);
        onSubmit({email, password});
    }

    useEffect(() => {
        if (!context.userContext.user) return;
        const _user = context.userContext.user;
        setGaUserId(_user.id);
        const firstAccount = _user.accounts.length ? _user.accounts[0] : null;
        setSentryUser(_user, firstAccount);
        if (firstAccount) {
            if (redirectURL.current) navigate(redirectURL.current);
            else navigate(DASHBOARD_ROUTE);
        } else navigate('/account-instellen');
    }, [context.userContext.user]);

    useEffect(() => {
        sendGaPageView(LOGIN_ROUTE);
        checkDemoLogin();
    }, []);

    return (
        <>
            <Login
                onSubmit={onLogin}
                onChange={onChange}
                email={email}
                password={password}
                loading={loading}
                errors={errors}
                onFocusField={onFocusField}
                onClickRegister={onClickRegister}
                onGoogleLogin={onGoogleLogin}
                onClickGoogle={onClickGoogle}
                onClickReset={onClickReset}
                onGoogleError={onGoogleError}
            />
            <ValidateTwoFactorController onSuccess={onTwoFactorSuccess} ref={mfaRef} />
            <IpVerificationController onSuccess={onTwoFactorSuccess} ref={ipVerificationRef} />
        </>
    );
}
